// App-level settler for owed deferred trade fees (sellFeeLedger.ts) — a
// sell's and, since 2026-11-02, a buy's: neither rides in the swap's external.
//
// The pair route's execution machine sends the 1% right after a trade's fill
// is seen. When that send fails, or the tab closes before its proof, the fee
// stays owed on the server; this hook — mounted for the whole app — signs the
// retry the next time this browser has the wallet open, on any page, once the
// server says a new signature is safe. Signing only from the silent device
// copy of the seed: it never prompts the vault.
//
// Light by design: it polls the ledger with a plain read and loads the TON
// signer only when there is something to sign, so it stays out of the boot
// graph.

import { useEffect, useRef } from 'react'
import type { UserlandWallet } from '../api/userland'
import { ledgerAbsent, listOwedSellFees, queuedSellFees } from '../lib/sellFeeLedger'

const FIRST_PASS_MS = 5_000
const PASS_INTERVAL_MS = 60_000
const LEDGER_ABSENT_BACKOFF_MS = 10 * 60_000

/** One pass; resolves to the delay before the next one. */
export async function sellFeeSettlerPass(
  address: string,
  balanceNano: string | null
): Promise<number> {
  try {
    const due =
      queuedSellFees(address).length > 0 ||
      (await listOwedSellFees(address)).some((row) => row.retry_ok)
    if (!due) return PASS_INTERVAL_MS
    const [{ retryOwedSellFees }, { loadDeviceWalletSeed }, { acquireTradeSendSlot }] =
      await Promise.all([
        import('../lib/sellProceedsFee'),
        import('../lib/deviceWalletSeed'),
        import('../lib/tradeSendQueue'),
      ])
    await retryOwedSellFees({
      walletAddress: address,
      loadSeed: () => loadDeviceWalletSeed(address),
      balanceNano: balanceNano && /^[0-9]+$/.test(balanceNano) ? BigInt(balanceNano) : null,
      acquireSlot: () => acquireTradeSendSlot(address),
    })
    return PASS_INTERVAL_MS
  } catch (err) {
    return ledgerAbsent(err) ? LEDGER_ABSENT_BACKOFF_MS : PASS_INTERVAL_MS
  }
}

export function useSellFeeSettler(wallet: UserlandWallet | null, balanceNano: string | null): void {
  const balanceRef = useRef(balanceNano)
  balanceRef.current = balanceNano
  const address = wallet?.activation_status === 'active' ? wallet.address : null

  useEffect(() => {
    if (!address) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const pass = async () => {
      if (stopped) return
      const delay = await sellFeeSettlerPass(address, balanceRef.current)
      if (!stopped) timer = setTimeout(pass, delay)
    }
    timer = setTimeout(pass, FIRST_PASS_MS)
    return () => {
      stopped = true
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [address])
}
